import dayjs from 'dayjs';
import { VAST_THUMBNAIL, WEBVIEW_THUMBNAIL } from './campaignThumbnails';
import {
  CREATIVE_TYPE,
  isUrlCreative,
  normalizeCreativeType,
} from '@/shared/constants/creativeTypes';
import { autoDetectAspectRatio } from '@/shared/utils';
import {
  buildWorkingHours,
  buildSlotDayTimeParting,
} from './schedulingTransformer';
import { CAMPAIGN_CREATIVE_PAYLOAD_TYPE } from '../constants';

const getRatioSegment = (screen: any): string => {
  const width = screen.resolution?.width ?? screen.width;
  const height = screen.resolution?.height ?? screen.height;
  const aspectRatio =
    width && height ? autoDetectAspectRatio(width, height) : '16:9';

  return `RATIO_${String(aspectRatio || '16:9').replace(':', '_')}`;
};

const getUrlCreativeKey = (url: string, type: string, screen: any): string =>
  `${type}:${url}:${getRatioSegment(screen)}`;

const generateUrlCreativeId = (folderId: string, screen: any): string =>
  `${folderId}_FILE_${crypto.randomUUID()}_${getRatioSegment(screen)}`;

const getUrlCreativeSrc = (creative: { src?: unknown }): string =>
  typeof creative.src === 'string' ? creative.src.trim() : '';

export const buildCampaignPayload = (
  campaign: any,
  isProgrammatic: boolean
) => {
  return {
    ...campaign,
    startDate:
      dayjs(campaign.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
    endDate: dayjs(campaign.endDate).format('YYYY-MM-DD') + 'T23:59:59.000Z',
    type: isProgrammatic
      ? CAMPAIGN_CREATIVE_PAYLOAD_TYPE.PROGRAMMATIC
      : CAMPAIGN_CREATIVE_PAYLOAD_TYPE.NON_PROGRAMMATIC,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

export const buildVastAndWebPayload = (
  selectedScreens: any[],
  partnerId: string,
  brandFolderId: string,
  scheduling?: any,
  campaignUseFallback?: boolean,
  brandFolderName?: string
) => {
  const assignSlotIndices = (slots: any[]): any[] => {
    const taken = new Set<number>(
      slots.map(s => s.index).filter((i): i is number => i != null)
    );
    let next = 0;
    return slots.map(slot => {
      if (slot.index != null) return slot;
      while (taken.has(next)) next++;
      taken.add(next);
      return { ...slot, index: next++ };
    });
  };

  const creativesMap = new Map();

  for (let i = 0; i < selectedScreens.length; i++) {
    const screen = selectedScreens[i];
    const slots = screen?.slots;

    for (let j = 0; j < slots.length; j++) {
      const slot = slots[j];
      const slotCreatives = slot.creatives || [];

      slotCreatives.forEach((creative: any) => {
        const creativeType = creative?.type;

        if (isUrlCreative(creativeType)) {
          const url = getUrlCreativeSrc(creative);
          const creativeKey = getUrlCreativeKey(url, creativeType, screen);
          if (url && !creativesMap.has(creativeKey)) {
            const existingId = creative.id;
            const id =
              existingId || generateUrlCreativeId(brandFolderId, screen);
            creativesMap.set(creativeKey, id);
          }
        }
      });
    }
  }

  const allCreatives: any[] = [];

  const screens = selectedScreens.map((screen: any) => {
    const resolvedPartnerId = partnerId;

    // New screen = no originalSlotIds stamp from EditCampaignFlow
    const isNewScreen = !screen.originalSlotIds;

    const mappedSlots = assignSlotIndices(screen.slots ?? []).map(
      (slot: any) => {
        const currentIds = (slot.creatives ?? [])
          .map((creative: any) => {
            const creativeType = creative?.type;
            if (isUrlCreative(creativeType)) {
              const url = getUrlCreativeSrc(creative);
              return url
                ? creativesMap.get(getUrlCreativeKey(url, creativeType, screen))
                : undefined;
            }
            return creative.id;
          })
          .filter(Boolean)
          .sort()
          .join(',');
        const originalIds = slot.originalCreativeIds
          ? [...slot.originalCreativeIds].sort().join(',')
          : null;
        const editStatus =
          originalIds === null
            ? 'NEW'
            : currentIds === originalIds
              ? 'STABLE'
              : 'EDIT';

        return {
          name: slot.name,
          index: slot.index,
          editStatus,
          dayTimeParting: scheduling
            ? buildSlotDayTimeParting(scheduling)
            : slot.dayTimeParting || {},
          creatives: (slot.creatives ?? [])
            .map((creative: any) => {
              const originalIds = slot.originalCreativeIds ?? [];
              const creativeType = creative?.type;
              // Case 1: slot was empty → all NEW
              // Case 2: per-creative check — in original set = STABLE, else NEW.
              // Deliberately does NOT force every creative to NEW just because the
              // slot's creative count changed (e.g. 1 → many, going from single to
              // alternating) — a creative that's genuinely unchanged should read as
              // STABLE regardless of how many siblings joined or left it; the slot's
              // own editStatus (EDIT) already signals "this slot's makeup changed".
              const allNew = originalIds.length === 0;
              const creativeEditStatus = allNew
                ? 'NEW'
                : originalIds.includes(
                      isUrlCreative(creativeType)
                        ? creativesMap.get(
                            getUrlCreativeKey(
                              getUrlCreativeSrc(creative),
                              creativeType,
                              screen
                            )
                          )
                        : creative.id
                    )
                  ? 'STABLE'
                  : 'NEW';
              let transformedCreative: any = null;

              if (isUrlCreative(creativeType)) {
                const url = getUrlCreativeSrc(creative);

                if (!url || url === '') {
                  return null;
                }

                const vastWebId = creativesMap.get(
                  getUrlCreativeKey(url, creativeType, screen)
                );
                const vastWebName =
                  creative.name ||
                  `${screen.name || 'Screen'} - ${slot.name || `Slot ${slot.index}`} - ${creativeType}`;
                const vastWebAssetName =
                  brandFolderName || creative.assetName || vastWebName;
                const vastWebW = screen.resolution?.width || screen.width || 0;
                const vastWebH =
                  screen.resolution?.height || screen.height || 0;
                transformedCreative = {
                  id: vastWebId,
                  creativeAssetId: vastWebId,
                  partnerId: resolvedPartnerId,
                  type: creativeType,
                  name: vastWebName,
                  assetName: vastWebAssetName,
                  src: url,
                  duration:
                    creative.duration ??
                    creative.src?.duration ??
                    screen.slotTimeInSec,
                  aspectRatio: autoDetectAspectRatio(vastWebW, vastWebH),
                  resolutionWidth: vastWebW,
                  resolutionHeight: vastWebH,
                  useFallback:
                    typeof campaignUseFallback === 'boolean'
                      ? campaignUseFallback
                      : creative.useFallback === true,
                  ...(creative.fallbackCreativeId && {
                    fallbackCreativeId: creative.fallbackCreativeId,
                  }),
                  createdAt: creative.createdAt || new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                };
              } else if (
                normalizeCreativeType(creativeType) === CREATIVE_TYPE.DEFAULT
              ) {
                const src = creative?.src || {};
                const fileId = src?.id;
                const assetId =
                  creative.creativeAssetId || src?.creativeAssetId;

                if (!fileId || !assetId) {
                  return null;
                }

                const fileSrc = src?.src || src?.url || '';

                if (!fileSrc) {
                  return null;
                }

                const fileType =
                  src?.type ||
                  (fileSrc.toLowerCase().endsWith('.mp4') ||
                  fileSrc.toLowerCase().endsWith('.mov') ||
                  fileSrc.toLowerCase().endsWith('.mkv')
                    ? 'VIDEO'
                    : 'IMAGE');

                const aspectRatio = src?.aspectRatio || '16:9';

                const resolutionWidth =
                  src?.resolutionWidth || src?.resolution?.width || 0;
                const resolutionHeight =
                  src?.resolutionHeight || src?.resolution?.height || 0;

                const creativeName = src?.name || creative.name || 'Creative';

                transformedCreative = {
                  id: fileId,
                  creativeAssetId: assetId,
                  partnerId: resolvedPartnerId,
                  type: fileType,
                  name: creativeName,
                  src: fileSrc,
                  duration:
                    src?.duration || creative.duration || screen.slotTimeInSec,
                  aspectRatio: aspectRatio,
                  assetName: creativeName,
                  resolutionHeight: resolutionHeight,
                  resolutionWidth: resolutionWidth,
                  createdAt: src?.createdAt || new Date().toISOString(),
                  updatedAt: src?.updatedAt || new Date().toISOString(),
                };
              } else {
                const flatId = creative.id || creative.creativeId;
                const flatAssetId =
                  creative.creativeAssetId ||
                  creative.src?.creativeAssetId ||
                  flatId;

                transformedCreative = {
                  ...creative,
                  id: flatId,
                  creativeId: flatId,
                  creativeAssetId: flatAssetId,
                  name: creative.name || creative.assetName || 'Creative',
                };
              }

              if (transformedCreative) {
                allCreatives.push({
                  ...transformedCreative,
                  editStatus: creativeEditStatus,
                });
              }

              return transformedCreative
                ? { ...transformedCreative, editStatus: creativeEditStatus }
                : null;
            })
            .filter(Boolean),
        };
      }
    );

    const originalSlotCount = screen.originalSlotIds?.length ?? null;
    const slotCountChanged =
      originalSlotCount !== null && mappedSlots.length !== originalSlotCount;

    const screenEditStatus = isNewScreen
      ? 'NEW'
      : slotCountChanged ||
          mappedSlots.some(
            s => s.editStatus === 'NEW' || s.editStatus === 'EDIT'
          )
        ? 'EDIT'
        : 'STABLE';
    return {
      id: screen.id,
      name: screen.name,
      deviceCode: screen.deviceCode,
      partnerId: resolvedPartnerId,
      workingHours: buildWorkingHours(screen.screenOnTime, screen.screenOnDays),

      adUnit: screen.adUnit,
      adUnitId: screen.adUnitId,
      resolution: screen.resolution || {
        width: screen.width,
        height: screen.height,
      },
      address: screen.address,
      numberOfSlots: screen.numberOfSlots,
      slotTimeInSec: screen.slotTimeInSec,
      numberOfBookedSlots: screen.numberOfBookedSlots || 0,
      editStatus: screenEditStatus,
      slots: mappedSlots,
    };
  });

  return { screens, creatives: allCreatives };
};

export const transformCreateCampaignToApiFormat = (campaign: any): any => {
  const brandFolderId = campaign.selectedBrand?.id;
  const {
    selectedScreens,
    creatives: uploadedCreatives,
    partnerId,
    ...rest
  } = campaign;

  const vastWebCreativesMap = new Map<string, any>();
  const imageVideoCreativesMap = new Map<string, any>();

  const transformedCreatives = (uploadedCreatives || []).map((c: any) => {
    const filesObj = c.files || {};

    const transformedFiles: Record<string, any> = {};

    Object.entries(filesObj).forEach(([aspectRatio, file]: [string, any]) => {
      if (file && typeof file === 'object') {
        let resolutionWidth = 0;
        let resolutionHeight = 0;

        if (file.resolution) {
          if (typeof file.resolution === 'string') {
            const [w, h] = file.resolution.split('x').map(Number);
            resolutionWidth = w || 0;
            resolutionHeight = h || 0;
          } else if (typeof file.resolution === 'object') {
            resolutionWidth = file.resolution.width || 0;
            resolutionHeight = file.resolution.height || 0;
          }
        } else if (file.width !== undefined || file.height !== undefined) {
          resolutionWidth = file.width || 0;
          resolutionHeight = file.height || 0;
        }

        const fileSrc = file.src || file.url || '';
        const fileType =
          file.type ||
          (fileSrc.endsWith('.mp4') || fileSrc.endsWith('.mov')
            ? 'VIDEO'
            : 'IMAGE');

        transformedFiles[aspectRatio] = {
          id:
            file.id ||
            `${Date.now()}_${partnerId}_ASSET_FILE_RATIO_${aspectRatio.replace(':', '_')}`,
          src: fileSrc,
          size: file.size || 0,
          type: fileType,
          status: 'READY',
          aspectRatio: aspectRatio,
          isAspectRatio: false,
          creativeAssetId:
            c.id || c.creativeId || `${Date.now()}_${partnerId}_ASSET`,
          resolutionWidth: resolutionWidth,
          resolutionHeight: resolutionHeight,
        };
      }
    });

    const creativeId =
      c.id || c.creativeId || `${Date.now()}_${partnerId}_ASSET`;
    const transformedCreative = {
      id: creativeId,
      src: {
        files: transformedFiles,
      },
      name: c.name || 'Creative',
      type: c.type || 'DEFAULT',
      duration: c.duration || '30s',
      partnerId: partnerId || c.partnerId,
      creativeId: creativeId,
      resolution: null,
      media: {},
    };

    if (creativeId) {
      imageVideoCreativesMap.set(creativeId, transformedCreative);
    }

    return transformedCreative;
  });

  (selectedScreens || []).forEach((screen: any, screenIndex: number) => {
    (screen.slots || []).forEach((slot: any, slotIndex: number) => {
      (slot.creatives || []).forEach((slotCreative: any) => {
        const creativeType = slotCreative?.type;
        const src = slotCreative?.src || {};
        if (isUrlCreative(creativeType)) {
          const url = String(slotCreative.url || '').trim();

          const creativeKey = getUrlCreativeKey(url, creativeType, screen);
          if (url && !vastWebCreativesMap.has(creativeKey)) {
            const creativeId =
              slotCreative.id || generateUrlCreativeId(brandFolderId, screen);

            const screenName = screen?.name || 'Screen';
            const slotName = slot?.name || `Slot ${slotIndex + 1}`;
            const generatedName = `${screenName} - ${slotName} - ${creativeType}`;

            const thumbnailUrl =
              creativeType === CREATIVE_TYPE.VAST
                ? VAST_THUMBNAIL
                : WEBVIEW_THUMBNAIL;

            const vastWebCreative = {
              id: creativeId,
              creativeId: creativeId,
              creativeAssetId: creativeId,
              url: url,
              name: slotCreative.name || generatedName,
              type: creativeType,
              duration: screen.slotTimeInSec
                ? `${screen.slotTimeInSec}s`
                : null,
              partnerId: partnerId,
              resolution: null,
              media: {},
              src: {
                files: {
                  thumbnail: {
                    id: `${creativeId}_FILE_RATIO_thumbnail`,
                    src: thumbnailUrl,
                    size: 0,
                    type: creativeType,
                    status: 'READY',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    aspectRatio: 'thumbnail',
                    isAspectRatio: false,
                    creativeAssetId: creativeId,
                    resolutionWidth: 0,
                    resolutionHeight: 0,
                  },
                },
              },
            };

            vastWebCreativesMap.set(creativeKey, vastWebCreative);
          }
        } else if (
          (slotCreative.type === CREATIVE_TYPE.DEFAULT ||
            normalizeCreativeType(slotCreative.type) ===
              CREATIVE_TYPE.DEFAULT) &&
          src?.files &&
          typeof src.files === 'object' &&
          Object.keys(src.files).length > 0
        ) {
          const creativeId =
            slotCreative.id ||
            slotCreative.src?.id ||
            slotCreative.src?.creativeId ||
            `${Date.now()}_${partnerId}_ASSET_${screenIndex}_${slotIndex}`;

          if (!imageVideoCreativesMap.has(creativeId)) {
            const filesObj = src.files;
            const transformedFiles: Record<string, any> = {};

            Object.entries(filesObj).forEach(
              ([aspectRatio, file]: [string, any]) => {
                if (file && typeof file === 'object') {
                  let resolutionWidth = 0;
                  let resolutionHeight = 0;

                  if (file.resolution) {
                    if (typeof file.resolution === 'string') {
                      const [w, h] = file.resolution.split('x').map(Number);
                      resolutionWidth = w || 0;
                      resolutionHeight = h || 0;
                    } else if (typeof file.resolution === 'object') {
                      resolutionWidth = file.resolution.width || 0;
                      resolutionHeight = file.resolution.height || 0;
                    }
                  } else if (
                    file.width !== undefined ||
                    file.height !== undefined
                  ) {
                    resolutionWidth = file.width || 0;
                    resolutionHeight = file.height || 0;
                  }

                  const fileSrc = file.src || file.url || '';
                  const fileType =
                    file.type ||
                    (fileSrc.endsWith('.mp4') || fileSrc.endsWith('.mov')
                      ? 'VIDEO'
                      : 'IMAGE');

                  transformedFiles[aspectRatio] = {
                    id:
                      file.id ||
                      `${creativeId}_FILE_RATIO_${aspectRatio.replace(':', '_')}`,
                    src: fileSrc,
                    size: file.size || 0,
                    type: fileType,
                    status: 'READY',
                    aspectRatio: aspectRatio,
                    isAspectRatio: false,
                    creativeAssetId: creativeId,
                    resolutionWidth: resolutionWidth,
                    resolutionHeight: resolutionHeight,
                  };
                }
              }
            );

            const imageVideoCreative = {
              id: creativeId,
              src: {
                files: transformedFiles,
              },
              name:
                slotCreative.name ||
                slotCreative.src?.name ||
                src?.name ||
                'Creative',
              type: 'DEFAULT',
              duration:
                slotCreative.duration || screen.slotTimeInSec
                  ? `${screen.slotTimeInSec}s`
                  : '30s',
              partnerId: partnerId,
              createdAt: slotCreative.createdAt,
              creativeId: creativeId,
              resolution: null,
              media: {},
            };

            imageVideoCreativesMap.set(creativeId, imageVideoCreative);
          }
        }
      });
    });
  });

  const creativeIdSet = new Set<string>();
  const allCreatives: any[] = [];

  transformedCreatives.forEach((creative: any) => {
    const id = creative.id || creative.creativeId;
    if (id && !creativeIdSet.has(id)) {
      creativeIdSet.add(id);
      allCreatives.push(creative);
    }
  });

  Array.from(imageVideoCreativesMap.values()).forEach((creative: any) => {
    const id = creative.id || creative.creativeId;
    if (id && !creativeIdSet.has(id)) {
      creativeIdSet.add(id);
      allCreatives.push(creative);
    }
  });

  Array.from(vastWebCreativesMap.values()).forEach((creative: any) => {
    const id = creative.id || creative.creativeId;
    if (id && !creativeIdSet.has(id)) {
      creativeIdSet.add(id);
      allCreatives.push(creative);
    }
  });

  const transformedScreens = (selectedScreens || []).map((screen: any) => ({
    ...screen,
    price: screen.price || 0,
    slots: (screen.slots || []).map((slot: any, slotIndex: number) => ({
      name: slot.name,
      index: slotIndex,
      creatives: (slot.creatives || [])
        .map((slotCreative: any) => {
          const creativeType = slotCreative?.type;
          if (isUrlCreative(creativeType)) {
            const url = String(slotCreative.url || '').trim();
            const vastWebCreative = vastWebCreativesMap.get(
              getUrlCreativeKey(url, creativeType, screen)
            );
            if (vastWebCreative) {
              return {
                id: vastWebCreative.id,
                type: creativeType,
                partnerId: partnerId,
              };
            }
          }
          if (
            normalizeCreativeType(slotCreative.type) === CREATIVE_TYPE.DEFAULT
          ) {
            return {
              id: slotCreative.id || slotCreative.src?.id,
              type: 'DEFAULT',
              partnerId: partnerId,
            };
          }

          return slotCreative;
        })
        .filter(Boolean),
    })),
  }));

  return {
    ...rest,
    partnerId: partnerId,
    screens: transformedScreens,
    creatives: allCreatives,
  };
};
